import React, { useState, useRef, useCallback, useEffect } from "react";
import { Loader2, ImagePlus, Mic, Sparkles, Wand2, Download, RotateCcw, X, Square, Upload, FolderOpen, Save, Trash2, Share2 } from "lucide-react";
import { ShareVideoSheet } from "./ShareVideoSheet";
import { FeedVideo } from "./FeedVideo";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const VOICES = [
  { key: "warm_female", label: "Warm female" },
  { key: "warm_male", label: "Warm male" },
  { key: "neutral", label: "Neutral narrator" },
];

export function RespawnerStudio() {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState("compose"); // compose | review | processing | done
  const [prompt, setPrompt] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceChoice, setVoiceChoice] = useState("warm_female");
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [description, setDescription] = useState("");
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [videoPath, setVideoPath] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [shareVideo, setShareVideo] = useState(null);
  const imgInput = useRef(null);
  const voiceInput = useRef(null);
  const pollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const recTimerRef = useRef(null);

  const stopRecord = () => {
    clearInterval(recTimerRef.current);
    setRecording(false);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { /* noop */ }
  };

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = (chunksRef.current[0] && chunksRef.current[0].type) || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        setVoiceFile(new File([blob], "live-recording.webm", { type }));
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true); setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => {
        if (s >= 29) { stopRecord(); return 30; }
        return s + 1;
      }), 1000);
    } catch (e) {
      toast.error("Couldn't access your microphone. Please allow mic permission.");
    }
  };

  const onImage = (f) => {
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const { data } = await api.get("/respawner/jobs");
      setJobs((data || []).filter((j) => j.status === "done" && j.video_path));
    } catch (e) { /* noop */ } finally { setLoadingJobs(false); }
  }, []);

  // Restore the user's single saved draft (photo + audio + text) on open.
  const hydrate = useCallback(async () => {
    try {
      const { data } = await api.get("/respawner/workspace");
      if (!data.exists) { setHasSaved(false); return; }
      setHasSaved(true);
      setPrompt(data.prompt || "");
      setDescription(data.description || "");
      setTranscript(data.transcript || "");
      setVoiceChoice(data.voice_choice || "warm_female");
      if (data.image_path) {
        try {
          const r = await fetch(fileUrl(data.image_path));
          const blob = await r.blob();
          const f = new File([blob], "portrait", { type: blob.type || data.image_ct || "image/png" });
          setImageFile(f); setImagePreview(URL.createObjectURL(blob));
        } catch (e) { /* ignore */ }
      }
      if (data.audio_path) {
        try {
          const r = await fetch(fileUrl(data.audio_path));
          const blob = await r.blob();
          setVoiceFile(new File([blob], data.audio_name || "voice-sample", { type: blob.type || data.audio_ct || "audio/webm" }));
        } catch (e) { /* ignore */ }
      }
      toast.success("Restored your saved draft");
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  const saveWorkspace = async () => {
    if (!imageFile && !prompt.trim() && !transcript.trim() && !voiceFile) {
      toast.error("Nothing to save yet"); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt || "");
      fd.append("description", description || "");
      fd.append("transcript", transcript || "");
      fd.append("voice_choice", voiceChoice);
      if (imageFile) fd.append("image", imageFile);
      if (voiceFile) fd.append("voice_sample", voiceFile);
      await api.post("/respawner/workspace", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasSaved(true);
      toast.success("Saved — we'll bring this back next time");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save your draft");
    } finally { setSaving(false); }
  };

  const clearWorkspace = async () => {
    try { await api.delete("/respawner/workspace"); } catch (e) { /* noop */ }
    setHasSaved(false);
    reset();
    toast.success("Cleared your saved draft");
  };

  const draft = async () => {
    if (!imageFile) { toast.error("Add a photo first"); return; }
    if (!prompt.trim()) { toast.error("Describe what you want them to say/do"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/respawner/draft", { prompt: prompt.trim(), has_voice: !!voiceFile });
      setDescription(data.description);
      setTranscript(data.transcript);
      setStep("review");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not draft the script");
    } finally { setBusy(false); }
  };

  const poll = useCallback((id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/respawner/job/${id}`);
        if (data.status === "done") {
          clearInterval(pollRef.current);
          setVideoPath(data.video_path);
          setStep("done");
          refreshUser && refreshUser();
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          setError(data.error || "Generation failed.");
          setStep("review");
          toast.error(data.error || "Generation failed.");
        }
      } catch (e) { /* keep polling */ }
    }, 5000);
  }, [refreshUser]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const generate = async () => {
    if (!transcript.trim()) { toast.error("Transcript is empty"); return; }
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("description", description);
      fd.append("transcript", transcript.trim());
      fd.append("voice_choice", voiceChoice);
      fd.append("image", imageFile);
      if (voiceFile) fd.append("voice_sample", voiceFile);
      const { data } = await api.post("/respawner/generate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJobId(data.job_id);
      setStep("processing");
      poll(data.job_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start generation");
    } finally { setBusy(false); }
  };

  const reset = () => {
    pollRef.current && clearInterval(pollRef.current);
    stopRecord();
    setStep("compose"); setPrompt(""); setImageFile(null); setImagePreview(null);
    setVoiceFile(null); setDescription(""); setTranscript(""); setJobId(null); setVideoPath(null); setError(null);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="respawner-studio">
      <div className="flex items-center justify-between gap-2 mb-3 shrink-0">
        <div className="flex items-center gap-2">
          {!showGallery && (step === "compose" || step === "review") && (
            <>
              <button onClick={saveWorkspace} disabled={saving} data-testid="respawner-save"
                className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
                style={{ background: "var(--accent)", color: "#fff" }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
              {hasSaved && (
                <button onClick={clearWorkspace} data-testid="respawner-clear-saved"
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
                  style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                  <Trash2 size={13} /> Clear
                </button>
              )}
            </>
          )}
        </div>
        <button onClick={() => { const next = !showGallery; setShowGallery(next); if (next) loadJobs(); }}
          data-testid="respawner-my-content"
          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
          style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
          <FolderOpen size={14} className="gold-text" /> {showGallery ? "Back to studio" : "My Content"}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
      {showGallery ? (
        <div className="space-y-3" data-testid="respawner-gallery">
          {loadingJobs && <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>}
          {!loadingJobs && jobs.length === 0 && (
            <div className="text-center py-16">
              <FolderOpen size={30} className="gold-text mx-auto mb-3" />
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No videos yet — create your first one!</p>
            </div>
          )}
          {jobs.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {jobs.map((j) => (
                <div key={j.id} className="rounded-2xl overflow-hidden flex flex-col" style={{ border: "1px solid var(--border)", background: "var(--overlay)" }} data-testid="respawner-gallery-item">
                <FeedVideo path={j.video_path} autoplay={false} wrapClassName="" className="aspect-square object-contain" testid="respawner-gallery-video" />
                  {j.description && <p className="text-[11px] px-2.5 pt-2 truncate" style={{ color: "var(--text-secondary)" }}>{j.description}</p>}
                  <div className="grid grid-cols-2 gap-1.5 p-2 mt-auto">
                    <a href={fileUrl(j.video_path)} download data-testid="respawner-gallery-download"
                      className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold"
                      style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>
                      <Download size={14} className="gold-text" /> Save
                    </a>
                    <button onClick={() => setShareVideo(j.video_path)} data-testid="respawner-gallery-share"
                      className="flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold"
                      style={{ background: "var(--accent)", color: "#fff" }}>
                      <Share2 size={14} /> Share
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <>
      <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
        Bring a photo to life. Upload a portrait, describe what they should say, and we'll generate a short talking video (up to 30 seconds). <span className="gold-text font-semibold">175 credits</span> per video.
      </p>

      {/* STEP 1 — compose */}
      {step === "compose" && (
        <div className="space-y-4">
          <input ref={imgInput} type="file" accept="image/*" className="hidden" data-testid="respawner-image-input"
            onChange={(e) => onImage(e.target.files?.[0])} />
          <button onClick={() => imgInput.current?.click()} data-testid="respawner-image-btn"
            className="w-full rounded-2xl overflow-hidden grid place-items-center relative"
            style={{ aspectRatio: "1/1", maxHeight: 260, background: "var(--overlay)", border: "1px dashed var(--border)" }}>
            {imagePreview ? (
              <img src={imagePreview} alt="portrait" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center">
                <ImagePlus size={30} className="gold-text mx-auto mb-2" />
                <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Add a portrait photo</span>
              </div>
            )}
          </button>

          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>What should they say or do?</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} data-testid="respawner-prompt"
              placeholder="e.g. Grandpa wishing everyone a happy reunion and telling them he loves them"
              className="mt-1.5 w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
          </div>

          {/* optional voice: upload OR record live */}
          <input ref={voiceInput} type="file" accept="audio/*" className="hidden" data-testid="respawner-voice-input"
            onChange={(e) => setVoiceFile(e.target.files?.[0] || null)} />
          {voiceFile ? (
            <div className="flex items-center gap-2 rounded-xl px-3 py-2.5" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
              <Mic size={15} className="gold-text" />
              <span className="text-sm truncate flex-1">{voiceFile.name}</span>
              <button onClick={() => setVoiceFile(null)} data-testid="respawner-voice-clear"><X size={15} style={{ color: "var(--text-secondary)" }} /></button>
            </div>
          ) : recording ? (
            <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: "var(--overlay)", border: "1px solid var(--accent)" }} data-testid="respawner-recording">
              <span className="h-2.5 w-2.5 rounded-full animate-pulse shrink-0" style={{ background: "#FF5C6C" }} />
              <span className="text-sm flex-1" style={{ color: "var(--text-primary)" }}>Recording… {recSecs}s <span style={{ color: "var(--text-secondary)" }}>/ 30s</span></span>
              <button onClick={stopRecord} data-testid="respawner-record-stop"
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>
                <Square size={12} /> Stop
              </button>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => voiceInput.current?.click()} data-testid="respawner-voice-btn"
                  className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm"
                  style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                  <Upload size={15} /> Upload sample
                </button>
                <button onClick={startRecord} data-testid="respawner-record-btn"
                  className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm"
                  style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                  <Mic size={15} /> Record live
                </button>
              </div>
              <p className="text-xs mt-1.5 text-center" style={{ color: "var(--text-secondary)" }}>Optional — we'll clone your voice. Skip to pick a preset voice.</p>
            </div>
          )}

          <button onClick={draft} disabled={busy} data-testid="respawner-draft-btn"
            className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold"
            style={{ background: "var(--accent)", color: "#fff" }}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Draft the script with AI
          </button>
        </div>
      )}

      {/* STEP 2 — review */}
      {step === "review" && (
        <div className="space-y-4">
          {error && <div className="text-sm rounded-xl p-3" style={{ background: "rgba(255,92,108,0.12)", color: "#FF8A94", border: "1px solid rgba(255,92,108,0.3)" }} data-testid="respawner-error">{error}</div>}
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Video description</label>
            <p className="mt-1 text-sm italic" style={{ color: "var(--text-secondary)" }}>{description}</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Transcript (edit as you like)</label>
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value.slice(0, 480))} rows={6} data-testid="respawner-transcript"
              className="mt-1.5 w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }} />
            <div className="text-right text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{transcript.length}/480 · ~{Math.min(30, Math.round(transcript.length / 16))}s</div>
          </div>

          {!voiceFile && (
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Voice</label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {VOICES.map((v) => (
                  <button key={v.key} onClick={() => setVoiceChoice(v.key)} data-testid={`respawner-voice-${v.key}`}
                    className="rounded-xl py-2 text-xs font-medium transition-colors"
                    style={voiceChoice === v.key
                      ? { background: "var(--accent)", color: "#fff" }
                      : { background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => setStep("compose")} className="rounded-xl px-4 py-3 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>Back</button>
            <button onClick={generate} disabled={busy} data-testid="respawner-generate-btn"
              className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold"
              style={{ background: "var(--accent)", color: "#fff" }}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Generate video · 175 credits
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — processing */}
      {step === "processing" && (
        <div className="flex-1 grid place-items-center py-16 text-center" data-testid="respawner-processing">
          <div>
            <Loader2 size={34} className="animate-spin gold-text mx-auto mb-4" />
            <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Bringing your photo to life…</p>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Voicing the script and animating — this can take a minute or two.</p>
          </div>
        </div>
      )}

      {/* STEP 4 — done */}
      {step === "done" && videoPath && (
        <div className="space-y-4" data-testid="respawner-result">
          <FeedVideo path={videoPath} autoplay={false} testid="respawner-result-video" />
          <div className="grid grid-cols-2 gap-2">
            <a href={fileUrl(videoPath)} download data-testid="respawner-download"
              className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>
              <Download size={16} className="gold-text" /> Download
            </a>
            <button onClick={() => setShareVideo(videoPath)} data-testid="respawner-share"
              className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold" style={{ background: "var(--accent)", color: "#fff" }}>
              <Share2 size={16} /> Share
            </button>
          </div>
          <button onClick={reset} data-testid="respawner-new"
            className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
            <RotateCcw size={15} /> Create another
          </button>
        </div>
      )}
      </>
      )}
      </div>

      {/* Share sheet */}
      {shareVideo && <ShareVideoSheet videoPath={shareVideo} description={jobs.find((j) => j.video_path === shareVideo)?.description} transcript={jobs.find((j) => j.video_path === shareVideo)?.transcript || (shareVideo === videoPath ? transcript : "")} onClose={() => setShareVideo(null)} />}
    </div>
  );
}
